// SceneryBudgetConfig — Set13 §3.7: the GLOBAL scenery budget/quality knobs. The no-crash
// invariant (§3.7) is: peak scenery memory + per-frame instance count are functions of CAMERA
// STATE + a FIXED BUDGET, never of world area / region area / zoom. All caps are checked BEFORE
// allocation; exceeding a cap DEGRADES (lower threshold, coarser bucket) — never allocates, never
// throws. LRU tile eviction bounds the live set; amortised builds bound per-frame work.
//
// ⚠ THE plan's highest-risk footgun (§8): every one of these fields must be resolved through a
// VALIDATED DEFAULT (this class's ctor via `Object.assign`), NEVER compared bare against a
// possibly-absent persisted value. `n > undefined` is `false`, so a bare guard SILENTLY VANISHES
// and the allocation explosion appears far from the cause. Use `new SceneryBudgetConfig(persisted)`
// — never `persisted ?? new SceneryBudgetConfig()`.
export class SceneryBudgetConfig {
	constructor(data?: Partial<SceneryBudgetConfig>) { Object.assign(this, data); }

	/** Absolute cap on live instances (across all live tiles). ~250k covers every zoom at which a
	 *  tree is even 2 px wide (§3.7). */
	maxInstancesTotal = 300_000;
	/** Per-tile cap (a tile larger than this is split / thinned). */
	maxInstancesPerTile = 4_000;
	/** LRU cap on live tiles. */
	maxLiveTiles = 1_024;
	/** Absolute cap on atlas bytes (6 variants × 256² RGBA ≈ 1.6 MB; this is very generous). */
	maxAtlasBytes = 64 << 20;
	/** Full density at or below this m/px (rank threshold = keepFrac). */
	mppFadeStart = 1.5;
	/** Zero instances at or above this m/px. */
	mppFadeEnd = 6.0;
	/**
	 * Adaptive placement chunk (Option A — replaces the old per-frame `tileBuildsPerFrame`).
	 *
	 * On-demand (`frameloop="demand"`) only runs `useFrame` while a frame is rendering, and scenery
	 * drives its own advance by invalidating between tile-build batches. On a SLOW software
	 * renderer (vscode's integrated browser / SwiftShader) each render can take ~1.3 s, so batching
	 * only a few tiles per frame meant ~60 ms of placement per ~1.3 s render — placement crawled.
	 *
	 * This knob sets the adaptive RATIO of placement-time : render-time. Each frame the renderer
	 * places tiles until its accumulated placement time reaches `lastRenderMs * ratio` (so on a fast
	 * GPU it behaves much like the old small batches, and on a slow one it batches a whole render's
	 * worth of work into a single redraw). The RESULT is clamped to a hard-coded 3 s maximum
	 * (placement chunk ≤ 3 s) — beyond that the user could mistake the map for a fully hung /
	 * needs-terminating browser.
	 */
	placementRenderRatio = 1.5;

	// ── Set15 P7 (§3.8) — the 3D NEAR-tier (real InstancedMesh) knobs. Both are validated
	// defaults resolved before any comparison (§8 R1 — the `x > undefined === false` footgun).
	// The near tier is only active in 3D perspective mode; 2D ignores both. ──
	/** Set15 P7 — max distance from the eye (metres) within which a real near-tier InstancedMesh
	 *  is drawn instead of the (P4) far-tier billboard.  With a locked camera, "near" is a FIXED
	 *  set computed once; real meshes are expensive (~13–20 k tris each) so beyond this radius the
	 *  impostor (which at this distance is sub-pixel or still legible) carries the distance. */
	nearMeshMaxDistance = 300;
	/** Set15 P7 — the per-kind cap on near-tier real-mesh instances (the nearest instances win).
	 *  At ~13–20 k tris/instance, ~50–200 instances is affordable; the cap keeps the near tier
	 *  bounded regardless of how dense the placement got near the eye. */
	nearMeshMaxInstances = 200;

	/** Signature of the budget fields baked into the renderer's CACHED tile placement (rank-thinning
	 *  + per-tile cap). A change must invalidate the tile-union cache (2026-09-01). Excludes fields
	 *  that don't affect placement output (maxLiveTiles/maxAtlasBytes/placementRenderRatio/near-tier).
	 *  STATIC (not a method) so it can be called on a possibly-plain deserialized budget object
	 *  without first normalizing it through `new C()` — it reads only these four fields. */
	static placementSignature(budget: SceneryBudgetConfig): string {
		return [
			budget.maxInstancesTotal, budget.maxInstancesPerTile, budget.mppFadeStart, budget.mppFadeEnd,
		].map(v=>typeof v == "number" && Number.isFinite(v) ? v : "?").join("|");
	}
}

/** Resolve an optional/possibly-absent numeric budget field to a validated non-negative number,
 *  rejecting NaN/±Infinity. The single seam for the budget's numeric guards (§8). */
export function resolveBudgetNumber<T = number>(v: number|n|undefined, fallback: number|T): number|T {
	if (typeof v != "number" || !Number.isFinite(v)) return fallback;
	return v;
}

/** Zoom-bucketed tile size (§3.7 Dial 2): a tile spans ~128–256 screen px, so the viewport covers
 *  a bounded ~150–500 tiles at every zoom. Tile size is PLACEMENT-INVARIANT (§3.3), so re-bucketing
 *  changes buffer packing and nothing about where objects are. Extracted out of the renderer so the
 *  budget suite can assert the bounded-tiles property without a renderer. */
export function tileSizeForMpp(mpp: number, opts: {minTile?: number; maxTile?: number} = {}): number {
	const minTile = resolveBudgetNumber(opts.minTile, 128);
	const maxTile = resolveBudgetNumber(opts.maxTile, 2048);
	if (mpp <= 0) return minTile;
	let size = minTile;
	const target = 256 * mpp;
	while (size * 2 <= target && size < maxTile) size *= 2;
	return size;
}

/** Estimate the atlas byte footprint for a grid of `cols × rows` square cells at `cellSize`
 *  resolution (RGBA, 4 bytes/texel). The atlas-size guard's arithmetic — so both the tracker and
 *  the renderer's dispose path reason about the same number. */
export function estimateAtlasBytes(cols: number, rows: number, cellSize: number): number {
	const c = resolveBudgetNumber(cols, 1);
	const r = resolveBudgetNumber(rows, 1);
	const cs = resolveBudgetNumber(cellSize, 256);
	return c * r * cs * cs * 4;
}

/** The GLOBAL budget enforcement point (§3.7's no-crash invariant) — the single object through
 *  which the renderer allocates instance slots and atlas bytes, so that every cap is checked
 *  BEFORE allocation and crossing one DEGRADES (grants nothing extra) rather than allocating past
 *  cap or throwing. `SceneryPlacer` already enforces per-tile/per-instance caps + rank thinning at
 *  placement time; this tracker layers the cross-kind TOTAL caps + atlas-byte cap + per-frame
 *  amortisation on top, because those span multiple kinds and persist across tiles.
 *
 * Usage: ONE tracker lives for the renderer's lifetime. `beginFrame()` must be called once per
 *  frame (it re-opens the per-frame instance allowance from `maxInstancesTotal`); `reserveInstances`
 *  grants slots toward that allowance and returns how many were granted (callers push that many);
 *  `tryReserveAtlas` / `releaseAtlas` bookkeep the PERSISTENT atlas-byte budget (a bake holds bytes
 *  until its kind is disposed, unlike instances which are per-frame). Every guard resolves the
 *  budget field through a validated default (§8) — never a bare comparison. */
export class SceneryBudgetTracker {
	config: SceneryBudgetConfig;

	/** Live atlas bytes currently reserved (persists across frames, released on kind dispose). */
	private _atlasBytesUsed = 0;
	/** Instances still available THIS FRAME (reset each beginFrame from maxInstancesTotal). */
	private instancesThisFrame = 0;

	constructor(config: Partial<SceneryBudgetConfig>|SceneryBudgetConfig = {}) {
		this.config = new SceneryBudgetConfig(config as Partial<SceneryBudgetConfig>);
	}

	/** Re-open the per-frame instance allowance from the configured cap. Must be called once per
	 *  frame before any instance reservation. */
	beginFrame(): void {
		this.instancesThisFrame = resolveBudgetNumber(this.config.maxInstancesTotal, 300000);
	}

	/** Number of live instances still available this frame (after any reservations). */
	get instancesRemaining(): number { return Math.max(0, this.instancesThisFrame); }

	/** Ask for `n` instance slots toward the per-frame allowance. Returns the number actually
	 *  granted (≤ n, never negative, never past the frame cap) — the caller pushes exactly that
	 *  many instances. DEGRADES (returns fewer) when the cap is hit; never allocates past cap. */
	reserveInstances(n: number): number {
		if (n <= 0) return 0;
		const granted = Math.min(n, this.instancesThisFrame);
		this.instancesThisFrame -= granted;
		return Math.max(0, granted);
	}

	/** Try to reserve `bytes` of atlas memory for a bake. Returns true only if the bake fits within
	 *  the (validated) `maxAtlasBytes` cap; on success the bytes are held until {@link releaseAtlas}.
	 *  DEGRADES (returns false → the caller skips the bake) rather than allocating past cap. */
	tryReserveAtlas(bytes: number): boolean {
		const max = resolveBudgetNumber(this.config.maxAtlasBytes, 64 << 20);
		if (bytes <= 0) return true;
		if (this._atlasBytesUsed + bytes > max) return false;
		this._atlasBytesUsed += bytes;
		return true;
	}

	/** Release previously-reserved atlas bytes (e.g. when a kind's bake is disposed). Never raises
	 *  the used count; clamps at 0 (defensive against double-release). */
	releaseAtlas(bytes: number): void {
		this._atlasBytesUsed = Math.max(0, this._atlasBytesUsed - (bytes > 0 ? bytes : 0));
	}

	/** Live atlas bytes currently reserved. */
	get atlasBytesUsed(): number { return this._atlasBytesUsed; }

	/** The resolved max-live-tiles cap (LRU eviction key — validated, never a bare compare). */
	resolveMaxLiveTiles(): number {
		return resolveBudgetNumber(this.config.maxLiveTiles, 1024);
	}

	/** The resolved adaptive placement : render RATIO (Option A). Validated through a default of
	 *  1.5 (§8); a non-finite/absent value falls back to it. The renderer multiplies the measured
	 *  last-render time by this to derive the placement-chunk budget (clamped to 3 s there). */
	resolvePlacementRenderRatio(): number {
		return resolveBudgetNumber(this.config.placementRenderRatio, 1.5);
	}

	/** Set15 P7 — the resolved near-tier mesh draw distance (validated, never a bare compare).
	 *  0 = the near tier is DISABLED (everything is a far-tier billboard); a negative/NaN/absent
	 *  value falls back to the default (a positive radius). */
	resolveNearMeshMaxDistance(): number {
		const v = resolveBudgetNumber(this.config.nearMeshMaxDistance, 300);
		return v < 0 ? 300 : v;
	}

	/** Set15 P7 — the resolved per-kind near-tier instance cap (validated, never a bare compare).
	 *  0 = no near instances; a negative/NaN/absent value falls back to the default. */
	resolveNearMeshMaxInstances(): number {
		const v = resolveBudgetNumber(this.config.nearMeshMaxInstances, 200);
		return v < 0 ? 200 : v;
	}
}
